import React, { useCallback } from 'react';
import moment from 'moment';
import { Card, Divider } from '@sede-enterprise/shell-ds-react-framework';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import {
    formatDate,
    mmOrInch,
    mmOrMiles,
    round,
    getSiteType,
    getLastDate,
    getLastValue,
    findFirstNonZeroCorrosionRate,
    getExpressions,
    renderTemperatureCut
} from '../../util/helperFunctions';
import { helperTexts } from '../../util/helperTexts';
import { useEvalMetrics } from '../../util/hooks/useEvalMetrics';
import useFetchCMLInfo from './hooks/useFetchCMLInfo';
import InfoData from '../../components/InfoPanel/InfoData';
import InfoLabelWithTooltip from '../../components/InfoPanel/InfoLabelWithToolTip';
import * as R from "rambda";

const panel_end = moment();
const helpContents = helperTexts.cmlInfo;


const CMLInfoPanel = ({ units, cmlID, site, prodUnit }) => {
    const { isRhienland, isNorcoEast, isSyngas, isBonga, isScotfordHCU } = getSiteType(site, prodUnit);

    const MeasuredWallThicknessMetric = "MeasuredWallThickness";
    const PredictedWallThicknessMetric = 'LiveModelWallThickness'

    const findLastDate = (inputdata, element) => getLastDate(inputdata, cmlID, element);
    const findLastValue = (inputdata, element) => getLastValue(inputdata, cmlID, element);

    const expressions = useCallback(() => getExpressions(isRhienland), [isRhienland]);
    const eval_data = useEvalMetrics('CMLExpression', cmlID, null, expressions);
    const infopanel_data = useFetchCMLInfo(cmlID);
    const renderLocationDetails = () => (
        <div className='info-details'>
            <h4>Location</h4>
            <div className='info-details-body'>
                <div className='data-col'>
                    <InfoData
                        label='Site'
                        value={R.path(['siteName'], infopanel_data)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Prod Unit'
                        value={R.path(['plant'], infopanel_data)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Unit'
                        value={R.path(['unit'], infopanel_data)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Equipment'
                        value={R.path(['equipment'], infopanel_data)}
                        unitFormatter={null}
                    />
                    <div className='info-data'>
                        <p className='data-label'>Corrosion Loop</p>
                        <p className='data-value'>
                            <Link
                                className='link'
                                to={`/cls/${encodeURIComponent(
                                    R.path(['corrosionLoop'], infopanel_data)
                                )}/details`}
                            >
                                {R.path(['corrosionLoop'], infopanel_data)}
                            </Link>
                        </p>
                    </div>
                    <InfoData
                        label='CML'
                        value={R.path(['cmLocationName'], infopanel_data)}
                        unitFormatter={null}
                    />
                </div>
            </div>
        </div>
    );

    const renderGeneralDetails = () => (
        <div className='info-details'>
            <h4>General</h4>
            <div className='info-details-body'>
                <div className='data-col'>
                    <InfoData
                        label='Minimum Required wall thickness'
                        value={
                            units
                                ? round(
                                    0.0393700787 *
                                    R.path(['minimumWallThickness'], infopanel_data),
                                    3
                                )
                                : round(
                                    R.path(['minimumWallThickness'], infopanel_data),
                                    3
                                )
                        }
                        units={units}
                        unitFormatter={mmOrInch}
                    />
                    {!isSyngas && !isNorcoEast && !isBonga && !isScotfordHCU && (
                        <InfoData
                            label='Temperature Cut'
                            value={renderTemperatureCut(
                                R.path(["cut"], infopanel_data),
                                units
                            )}
                            unitFormatter={null}
                        />
                    )}
                    <InfoData
                        label='Material'
                        value={R.path(['material'], infopanel_data)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Position'
                        value={R.path(['position'], infopanel_data)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Fluid'
                        value={R.path(['fluid'], infopanel_data)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Pipe diameter'
                        value={units
                            ?
                            R.path(["pipeDiameterInch"], infopanel_data)
                            :
                            R.path(["pipeDiameter"], infopanel_data)
                        }
                        units={units}
                        unitFormatter={mmOrInch}
                    />
                </div>
            </div>
        </div>
    );

    const renderMeasurements = () => (
        <div className='info-details'>
            <h4>Measurements</h4>
            <div className='info-details-body'>
                <div className='data-col'>
                    <InfoData
                        label='Last measured wall thickness'
                        value={
                            units
                                ? round(0.0393700787 * findLastValue(eval_data, MeasuredWallThicknessMetric), 3)
                                : round(findLastValue(eval_data, MeasuredWallThicknessMetric), 3)
                        }
                        units={units}
                        date={
                            findLastDate(eval_data, MeasuredWallThicknessMetric)
                        }
                        unitFormatter={mmOrInch}
                    />
                </div>
            </div>
        </div>
    );

    const renderPredictions = () => (
        <div className='info-details'>
            <h4>Prediction</h4>
            <div className='info-details-body'>
                <div className='data-col'>
                    <div className='info-data'>
                        <InfoLabelWithTooltip
                            label='Current average wall thickness'
                            tooltipContent={helpContents.CurrentAvgWT}
                        />
                        <p className='data-value'>
                            {units
                                ? round(0.0393700787 * findLastValue(eval_data, PredictedWallThicknessMetric), 3)
                                : round(findLastValue(eval_data, PredictedWallThicknessMetric), 3)}{' '}
                            {mmOrInch(units)}{' '}
                            <span className='titleLabelDate'>{formatDate(new Date(panel_end))}</span>
                        </p>
                    </div>
                    <div className='info-data'>
                        <InfoLabelWithTooltip
                            label='Current average corrosion rate'
                            tooltipContent={helpContents.CurrentAvgCr}
                        />
                        <p className='data-value'>
                            {units
                                ? round(39.3700787402 * findFirstNonZeroCorrosionRate(
                                    R.path(
                                        ['result', cmlID, 'LiveModelCorrosionRate', 'data'],
                                        eval_data
                                    )
                                ),3)
                                : findFirstNonZeroCorrosionRate(
                                    R.path(
                                        ['result', cmlID, 'LiveModelCorrosionRate', 'data'],
                                        eval_data
                                    )
                                )}{' '}
                            {mmOrMiles(units)}{' '}
                            <span className='titleLabelDate'>{formatDate(new Date(panel_end))}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className='content-body'>
            <Card className='details-card' bodyPadding={false}>
                <div className='details-card-body'>
                    {renderLocationDetails()}
                    <div className='details-container'>
                        <Divider direction='vertical' className='vertical-divider' />
                        {renderGeneralDetails()}
                    </div>
                    <div className='details-container'>
                        <Divider direction='vertical' className='vertical-divider' />
                        {renderMeasurements()}
                    </div>
                    <div className='details-container'>
                        <Divider direction='vertical' className='vertical-divider' />
                        {renderPredictions()}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default CMLInfoPanel;